import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  Snackbar,
  Alert,
  AlertColor,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Slide,
  SlideProps,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutline,
  ErrorOutline,
  WarningAmberOutlined,
  InfoOutlined,
  Close,
} from '@mui/icons-material';

// Slide transition for Snackbar
const SlideTransition = (props: SlideProps) => {
  return <Slide {...props} direction="up" />;
};

// Types
export type NotificationType = 'success' | 'error' | 'warning' | 'info';

interface NotificationOptions {
  message: string;
  type?: NotificationType;
  duration?: number;
  title?: string;
}

interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: 'primary' | 'error' | 'warning' | 'success';
  icon?: 'warning' | 'error' | 'info' | 'success';
}

interface AlertOptions {
  title: string;
  message: string;
  content?: ReactNode;
  buttonText?: string;
  buttonColor?: 'primary' | 'error' | 'warning' | 'success';
  icon?: 'warning' | 'error' | 'info' | 'success';
}

interface NotificationContextType {
  // Notifications (replacing alert)
  showNotification: (options: NotificationOptions | string) => void;
  showSuccess: (message: string, title?: string) => void;
  showError: (message: string, title?: string) => void;
  showWarning: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;
  
  // Confirmation dialog (replacing confirm)
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  
  // Alert dialog (simple popup with Okay button)
  alert: (options: AlertOptions) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Icon mapping
const getIcon = (type: NotificationType | string) => {
  switch (type) {
    case 'success':
      return <CheckCircleOutline sx={{ fontSize: 28 }} />;
    case 'error':
      return <ErrorOutline sx={{ fontSize: 28 }} />;
    case 'warning':
      return <WarningAmberOutlined sx={{ fontSize: 28 }} />;
    case 'info':
    default:
      return <InfoOutlined sx={{ fontSize: 28 }} />;
  }
};

// Color mapping for confirm dialog
const getConfirmIconColor = (icon?: string) => {
  switch (icon) {
    case 'success':
      return 'success.main';
    case 'error':
      return 'error.main';
    case 'warning':
      return 'warning.main';
    case 'info':
    default:
      return 'info.main';
  }
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarTitle, setSnackbarTitle] = useState<string | undefined>();
  const [snackbarType, setSnackbarType] = useState<AlertColor>('info');
  const [snackbarDuration, setSnackbarDuration] = useState(4000);

  // Confirm dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmOptions, setConfirmOptions] = useState<ConfirmOptions>({
    title: '',
    message: '',
  });
  const [confirmResolve, setConfirmResolve] = useState<((value: boolean) => void) | null>(null);

  // Alert dialog state
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertOptions, setAlertOptions] = useState<AlertOptions>({
    title: '',
    message: '',
  });
  const [alertResolve, setAlertResolve] = useState<(() => void) | null>(null);

  // Show notification
  const showNotification = useCallback((options: NotificationOptions | string) => {
    if (typeof options === 'string') {
      setSnackbarMessage(options);
      setSnackbarType('info');
      setSnackbarTitle(undefined);
      setSnackbarDuration(4000);
    } else {
      setSnackbarMessage(options.message);
      setSnackbarType(options.type || 'info');
      setSnackbarTitle(options.title);
      setSnackbarDuration(options.duration || 4000);
    }
    setSnackbarOpen(true);
  }, []);

  const showSuccess = useCallback((message: string, title?: string) => {
    showNotification({ message, type: 'success', title });
  }, [showNotification]);

  const showError = useCallback((message: string, title?: string) => {
    showNotification({ message, type: 'error', title, duration: 6000 });
  }, [showNotification]);

  const showWarning = useCallback((message: string, title?: string) => {
    showNotification({ message, type: 'warning', title, duration: 5000 });
  }, [showNotification]);

  const showInfo = useCallback((message: string, title?: string) => {
    showNotification({ message, type: 'info', title });
  }, [showNotification]);

  // Handle snackbar close
  const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  // Show confirm dialog
  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmOptions(options);
      setConfirmResolve(() => resolve);
      setConfirmOpen(true);
    });
  }, []);

  // Handle confirm
  const handleConfirm = () => {
    setConfirmOpen(false);
    if (confirmResolve) {
      confirmResolve(true);
      setConfirmResolve(null);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setConfirmOpen(false);
    if (confirmResolve) {
      confirmResolve(false);
      setConfirmResolve(null);
    }
  };

  // Show alert dialog
  const alert = useCallback((options: AlertOptions): Promise<void> => {
    return new Promise((resolve) => {
      setAlertOptions(options);
      setAlertResolve(() => resolve);
      setAlertOpen(true);
    });
  }, []);

  // Handle alert close
  const handleAlertClose = () => {
    setAlertOpen(false);
    if (alertResolve) {
      alertResolve();
      setAlertResolve(null);
    }
  };

  const contextValue: NotificationContextType = {
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    confirm,
    alert,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={snackbarDuration}
        onClose={handleSnackbarClose}
        TransitionComponent={SlideTransition}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ maxWidth: 400 }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarType}
          variant="filled"
          sx={{
            width: '100%',
            alignItems: 'flex-start',
            '& .MuiAlert-icon': {
              fontSize: 24,
              mt: snackbarTitle ? 0.5 : 0,
            },
            '& .MuiAlert-message': {
              width: '100%',
            },
          }}
          action={
            <IconButton
              size="small"
              color="inherit"
              onClick={handleSnackbarClose}
              sx={{ mt: snackbarTitle ? -0.5 : 0 }}
            >
              <Close fontSize="small" />
            </IconButton>
          }
        >
          {snackbarTitle && (
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
              {snackbarTitle}
            </Typography>
          )}
          <Typography variant="body2">{snackbarMessage}</Typography>
        </Alert>
      </Snackbar>

      {/* Confirm Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={handleCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                color: getConfirmIconColor(confirmOptions.icon),
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {getIcon(confirmOptions.icon || 'warning')}
            </Box>
            <Typography variant="h6" fontWeight={600}>
              {confirmOptions.title}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.primary' }}>
            {confirmOptions.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
          <Button
            onClick={handleCancel}
            variant="outlined"
            color="inherit"
            sx={{ minWidth: 100 }}
          >
            {confirmOptions.cancelText || 'Cancel'}
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color={confirmOptions.confirmColor || 'primary'}
            autoFocus
            sx={{ minWidth: 100 }}
          >
            {confirmOptions.confirmText || 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Dialog */}
      <Dialog
        open={alertOpen}
        onClose={handleAlertClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                color: getConfirmIconColor(alertOptions.icon || 'success'),
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {getIcon(alertOptions.icon || 'success')}
            </Box>
            <Typography variant="h6" fontWeight={600}>
              {alertOptions.title}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.primary' }}>
            {alertOptions.message}
          </DialogContentText>
          {alertOptions.content}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={handleAlertClose}
            variant="contained"
            color={alertOptions.buttonColor || 'primary'}
            autoFocus
            sx={{ minWidth: 100 }}
          >
            {alertOptions.buttonText || 'Got it'}
          </Button>
        </DialogActions>
      </Dialog>
    </NotificationContext.Provider>
  );
};

// Hook to use notifications
export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationProvider;
